<template>
  <ClientOnly>
    <section
      v-if="campuses?.length"
      class="surface-panel surface-panel--elevated">
      <div class="container">
        <h2 class="campus__title">{{ title }}</h2>

        <div class="campus__map-container">
          <div class="campus__carousel">
            <!-- CARTE -->
            <div
              v-if="currentCampusHasMap"
              class="campus__map-wrapper">
              <div
                :id="`map-${currentCampusIndex}`"
                class="campus__map" />
            </div>

            <!-- FALLBACK (pas de coords ni géocodage) -->
            <div
              v-else
              class="campus__map-fallback">
              <div class="campus__fallback-content">
                <p class="campus__fallback-text">{{ currentCampus?.Name }}</p>
              </div>
            </div>

            <!-- NAVIGATION GAUCHE -->
            <button
              v-if="campuses.length > 1"
              class="campus__nav campus__nav--prev"
              :disabled="currentCampusIndex === 0"
              aria-label="Campus précédent"
              @click="previousCampus">
              ‹
            </button>

            <!-- NAVIGATION DROITE -->
            <button
              v-if="campuses.length > 1"
              class="campus__nav campus__nav--next"
              :disabled="currentCampusIndex === campuses.length - 1"
              aria-label="Campus suivant"
              @click="nextCampus">
              ›
            </button>
          </div>
        </div>

        <!-- ADRESSE EN BAS -->
        <div class="campus__address-footer">
          <span class="campus__address-text">{{ currentCampus?.Name }}</span>
          <span
            v-if="currentCampus?.Address.ZipCode || currentCampus?.Address.City"
            class="campus__address-text">
            {{ currentCampus?.Address.ZipCode }} {{ currentCampus?.Address.City }}
          </span>
        </div>

        <!-- INDICATEURS -->
        <div
          v-if="campuses.length > 1"
          class="campus__indicators">
          <button
            v-for="(campus, index) in campuses"
            :key="index"
            class="campus__indicator"
            :class="{ 'campus__indicator--active': index === currentCampusIndex }"
            :aria-label="`Aller au campus ${index + 1}`"
            @click="currentCampusIndex = index" />
        </div>
      </div>
    </section>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCampus } from '~/composables/useCampus'
import type { SchoolDataResponse } from '~/types/school'

interface Props {
  schoolData: SchoolDataResponse | null
}

const props = defineProps<Props>()

// Utilise le composable campus
const { currentCampusIndex, currentCampus, currentCampusHasMap, nextCampus, previousCampus } =
  useCampus(computed(() => props.schoolData?.parsedAddressesBlock || null))

// Computed pour les campus et le titre
const campuses = computed(() => props.schoolData?.parsedAddressesBlock?.Campuses || [])
const title = computed(() => props.schoolData?.parsedAddressesBlock?.Title || '')
</script>

<style scoped>
.campus__title {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-12);
  text-align: left;
}

/* ── Conteneur carte (pleine largeur sur mobile) ─────────────── */
.campus__map-container {
  width: 100%;
}

/* ── CARROUSEL ─────────────────────────────────────────────── */
.campus__carousel {
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.2);
  margin-bottom: var(--space-8);
}

.campus__map-wrapper {
  position: relative;
  z-index: 0;
  isolation: isolate;
  width: 100%;
  height: 100%;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.campus__map-wrapper :deep(.leaflet-container) {
  z-index: 0;
}

.campus__map {
  width: 100%;
  height: 100%;
}

/* ── FALLBACK (pas de géocodage) ─────────────────────────────────────────────── */
.campus__map-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: var(--radius-lg);
}

.campus__fallback-content {
  text-align: center;
  padding: var(--space-12);
}

.campus__fallback-text {
  font-size: var(--text-lg);
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* ── NAVIGATION FLÈCHES ─────────────────────────────────────────────── */
.campus__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: var(--radius-full);
  font-size: 28px;
  font-weight: 400;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--duration-fast) var(--ease-out);
  z-index: 10;
}

.campus__nav:hover:not(:disabled) {
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.campus__nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.campus__nav--prev {
  left: var(--space-4);
}

.campus__nav--next {
  right: var(--space-4);
}

/* ── ADRESSE EN BAS ─────────────────────────────────────────────── */
.campus__address-footer {
  background-color: rgba(0, 0, 0, 0.3);
  padding: var(--space-6);
  border-radius: var(--radius-md);
  text-align: center;
  margin-bottom: var(--space-8);
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
}

.campus__address-text {
  display: block;
  font-size: var(--text-sm);
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.campus__address-text:first-child {
  font-size: var(--text-base);
  font-weight: 700;
  color: white;
}

/* ── INDICATEURS (DOTS) ─────────────────────────────────────────────── */
.campus__indicators {
  display: flex;
  justify-content: center;
  gap: var(--space-3);
}

.campus__indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.4);
  border: none;
  cursor: pointer;
  transition: all var(--duration-fast) var(--ease-out);
  padding: 0;
}

.campus__indicator:hover {
  background-color: rgba(255, 255, 255, 0.6);
}

.campus__indicator--active {
  background-color: white;
  width: 28px;
  border-radius: 5px;
}

/* ── LEAFLET OVERRIDES ─────────────────────────────────────────────── */
:deep(.leaflet-control-attribution) {
  background-color: rgba(255, 255, 255, 0.8) !important;
  font-size: 11px !important;
}

:deep(.leaflet-marker-icon) {
  filter: hue-rotate(0deg) brightness(1.1);
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 768px) {
  .campus__title {
    font-size: var(--text-xl);
    margin-bottom: var(--space-8);
  }

  .campus__map-container {
    padding-inline: 0;
    margin-inline: calc(-1 * var(--container-pad));
    width: calc(100% + 2 * var(--container-pad));
  }

  .campus__carousel {
    height: 360px;
    margin-bottom: var(--space-6);
    border-radius: var(--radius-md);
  }

  .campus__nav {
    width: 40px;
    height: 40px;
    font-size: 24px;
  }

  .campus__address-footer {
    padding: var(--space-4);
    min-height: 50px;
  }

  .campus__address-text {
    font-size: var(--text-xs);
  }

  .campus__address-text:first-child {
    font-size: var(--text-sm);
  }

  .campus__indicator {
    width: 8px;
    height: 8px;
  }

  .campus__indicator--active {
    width: 24px;
  }
}

@media (max-width: 640px) {
  .campus__title {
    font-size: var(--text-lg);
    margin-bottom: var(--space-6);
  }

  .campus__carousel {
    height: 400px;
    margin-bottom: var(--space-4);
    border-radius: var(--radius-md);
  }

  .campus__nav {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }

  .campus__nav--prev {
    left: var(--space-2);
  }

  .campus__nav--next {
    right: var(--space-2);
  }

  .campus__address-footer {
    padding: var(--space-3);
    min-height: 45px;
    gap: var(--space-1);
  }

  .campus__address-text {
    font-size: var(--text-xs);
  }

  .campus__address-text:first-child {
    font-size: var(--text-sm);
  }

  .campus__indicator {
    width: 6px;
    height: 6px;
  }

  .campus__indicator--active {
    width: 20px;
  }
}
</style>
